import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Input } from '../../shared/ui/input';
import { FieldLabel } from '../../shared/ui/field-label';
import { Modal } from '../../shared/ui/modal';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import { customDomainUsage, type TCustomDomain } from '../../entities/custom-domain';

const Fact = ({ name, value }: { name: string; value: string }) => (
  <div className="flex justify-between border-b border-border-row py-2 text-meta">
    <span className="text-muted">{name}</span>
    <span className="text-primary">{value}</span>
  </div>
);

// In use, removing it breaks senders: the counts say who, and the name has to be typed.
export const RemoveDomainDialog = ({
  domain,
  waiting,
  onClose,
  onConfirm,
}: {
  domain: TCustomDomain | null;
  waiting: boolean;
  onClose: () => void;
  onConfirm: (domain: TCustomDomain) => void;
}) => {
  const [typed, setTyped] = useState('');
  const usage = useQuery({
    queryKey: ['custom-domain-usage', domain?.id],
    queryFn: () => customDomainUsage(domain?.id ?? ''),
    enabled: domain !== null,
  });
  const used = (usage.data?.requestsWeek ?? 0) > 0;

  return (
    <Modal
      open={domain !== null}
      onOpenChange={(open) => {
        if (!open) {
          setTyped('');
          onClose();
        }
      }}
      width="md"
      title={`Remove ${domain?.hostname ?? ''}?`}
      description={
        used
          ? 'Every address on it stops answering at once. Senders still using it get refused.'
          : 'Nothing was sent to it in the past week.'
      }
      submit={{
        label: 'Remove',
        onSubmit: () => domain && onConfirm(domain),
        disabled: !usage.data || (used && typed.trim().toLowerCase() !== domain?.hostname),
        waiting,
        waitingLabel: 'Removing',
      }}
    >
      {usage.isPending && <LoadingState rows={2} />}
      {usage.isError && <ErrorState error={usage.error} />}
      {usage.data && used && (
        <>
          <Fact
            name="Sent here in the past week"
            value={`${usage.data.requestsWeek.toLocaleString('en-US')} request${usage.data.requestsWeek === 1 ? '' : 's'}`}
          />
          {usage.data.sources.map((source) => (
            <Fact
              key={source.sourceId}
              name={source.name}
              value={source.requests.toLocaleString('en-US')}
            />
          ))}
          <Fact name="Their other addresses" value="keep working" />
          <p className="mt-3 text-meta text-primary">
            {usage.data.sources.length === 1 ? 'That sender is' : 'These senders are'} still posting
            here. Move them to another hostname first, or they will start failing.
          </p>
          <div className="mt-3">
            <FieldLabel htmlFor="remove-domain-confirm">Type the hostname to confirm</FieldLabel>
            <Input
              id="remove-domain-confirm"
              mono
              placeholder={domain?.hostname}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
          </div>
        </>
      )}
    </Modal>
  );
};
